import { queryClient } from "@/src/config/react-query";
import {
  GroupMemberRole,
  JoinType,
  MembershipStatus,
} from "@/src/constants/groups/group-membership";
import { GroupDetailsTab } from "@/src/constants/groups/group-tabs";
import { InvitableMember, UserProfile } from "@/src/models/user.model";
import {
  useCreateGroupMembership,
  useDeleteGroup,
  useGetGroupById,
  useGetGroupMemberCount,
  useGetGroupMemberIds,
  useGetLatestGroups,
  useGetMyMembership,
  useRemoveGroupMembership,
  useUpdateGroupMemberCount,
  useUpdateGroupMembership,
} from "@/src/services/api/groups/groups.hooks";
import { POSTS_QUERY_KEY } from "@/src/query-keys/post.keys";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const useGroupDetails = (id?: string, user?: UserProfile) => {
  const router = useRouter();
  const { t } = useTranslation();

  // Group Data
  const { data, isPending } = useGetGroupById(id as string);
  const { data: memberCount = 0 } = useGetGroupMemberCount(id as string);
  const { data: existingMemberIds = [] } = useGetGroupMemberIds(id as string);
  const group = data
    ? {
        id: data.ItemId,
        name: data.name,
        description: data.description,
        coverImageUrl: data.coverImageUrl,
        avatarLabel: data.name?.slice(0, 2).toUpperCase() ?? "NA",
        isPrivate: data.isPrivate ?? false,
        memberCount: memberCount,
        admin: data.CreatedBy,
      }
    : undefined;

  // Membership
  const { data: myMembership } = useGetMyMembership(
    id as string,
    user?.itemId ?? "",
  );
  const isMember = !!myMembership;
  const status = myMembership?.status;
  const isAdmin =
    myMembership?.role === GroupMemberRole.ADMIN ||
    group?.admin === user?.itemId;

  // Tabs
  const [activeTab, setActiveTab] = useState<GroupDetailsTab>(
    GroupDetailsTab.POSTS,
  );
  const handleTabChange = (tab: GroupDetailsTab) => setActiveTab(tab);

  // Latest groups (replaces mock suggestions) for the right-panel sidebar
  const { data: latestGroups = [] } = useGetLatestGroups(5);
  const handleSuggestion = (suggestionId: string) => {
    router.push(`/groups/${suggestionId}`);
  };

  // Toast
  const [toast, setToast] = useState<{
    text1: string;
    type: "success" | "error";
  } | null>(null);

  useEffect(() => {
    if (!toast || toast.type === "error") return;
    const timer = setTimeout(() => setToast(null), 1500);
    return () => clearTimeout(timer);
  }, [toast]);

  // Mutations
  const { mutateAsync: createGroupMembership } = useCreateGroupMembership();
  const { mutateAsync: updateGroupMembership } = useUpdateGroupMembership();
  const { mutateAsync: updateMemberCount } = useUpdateGroupMemberCount();
  const { mutateAsync: removeGroupMembership } = useRemoveGroupMembership();
  const { mutateAsync: deleteGroup } = useDeleteGroup();
  const { mutate: sendNotification } = useSendNotification();
  const [isActionPending, setIsActionPending] = useState(false);

  // Add members modal
  const [membersModalVisible, setMembersModalVisible] = useState(false);
  const [invitedMembers, setInvitedMembers] = useState<InvitableMember[]>([]);

  const handleJoinGroup = async () => {
    if (!user?.itemId || !id || isActionPending) return;
    setIsActionPending(true);
    try {
      const isPrivate = group?.isPrivate;

      const membership = await createGroupMembership({
        groupId: id,
        userId: user.itemId,
        role: GroupMemberRole.MEMBER,
        status: isPrivate ? MembershipStatus.PENDING : MembershipStatus.ACTIVE,
        joinType: isPrivate ? JoinType.REQUEST : JoinType.DIRECT_JOIN,
        groupUserSnapshot: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          profileImageUrl: user.profileImageUrl,
        },
        joinedAt: new Date().toLocaleDateString("en-GB"),
      });

      // Notify group admin about join request (private groups only)
      if (isPrivate && group?.admin) {
        sendNotification({
          userIds: [group.admin],
          denormalizedPayload: JSON.stringify({
            type: "join_request",
            message: t("NOTIFY_JOIN_REQUEST", {
              firstName: user.firstName,
              lastName: user.lastName,
              groupName: group.name,
            }),
            actorAvatar: user.profileImageUrl,
            actorName: `${user.firstName} ${user.lastName}`,
            actorId: user.itemId,
            groupId: id,
            membershipId: membership?.insertgroupmembership?.itemId,
            targetEntityId: id,
          }),
          configuratoinName: "post-liked",
          responseKey: "group-action",
          responseValue: "join_request",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["getGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount", id] });
      queryClient.invalidateQueries({ queryKey: ["getMyMembership", id] });
      queryClient.refetchQueries({ queryKey: ["getGroupMembers", id] });
      setToast({
        text1: t(isPrivate ? "LABEL_REQUEST_SENT" : "LABEL_JOINED"),
        type: "success",
      });
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  const handleAddMembers = async (members: InvitableMember[]) => {
    if (!id || isActionPending) return;
    setIsActionPending(true);
    try {
      await Promise.all(
        members.map((member) =>
          createGroupMembership({
            groupId: id,
            userId: member.itemId,
            role: GroupMemberRole.MEMBER,
            status: MembershipStatus.ACTIVE,
            joinType: JoinType.ADDED_BY_ADMIN,
            groupUserSnapshot: {
              firstName: member.firstName,
              lastName: member.lastName,
              email: member.email,
              profileImageUrl: member.profileImageUrl,
            },
            joinedAt: new Date().toLocaleDateString("en-GB"),
          }),
        ),
      );

      // Notify each added member
      if (members.length > 0) {
        sendNotification({
          userIds: members.map((m) => m.itemId),
          denormalizedPayload: JSON.stringify({
            type: "added_to_group",
            message: t("NOTIFY_ADDED_TO_GROUP", {
              firstName: user?.firstName,
              lastName: user?.lastName,
              groupName: group?.name,
            }),
            actorAvatar: user?.profileImageUrl,
            actorName: `${user?.firstName} ${user?.lastName}`,
            actorId: user?.itemId,
            groupId: id,
            targetEntityId: id,
          }),
          configuratoinName: "post-liked",
          responseKey: "group-action",
          responseValue: "added_to_group",
        });
      }

      await updateMemberCount(id);
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount", id] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberIds", id] });
      queryClient.refetchQueries({ queryKey: ["getGroupMembers", id] });
      setInvitedMembers([]);
      setToast({ text1: t("LABEL_MEMBERS_ADDED"), type: "success" });
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  const addMembership = async (membershipId: string) => {
    try {
      setIsActionPending(true);
      await updateGroupMembership({
        filter: JSON.stringify({ ItemId: membershipId }),
        status: MembershipStatus.ACTIVE,
      });
      queryClient.invalidateQueries({ queryKey: ["getGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount", id] });
      queryClient.invalidateQueries({ queryKey: ["getMyMembership", id] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberIds", id] });
      queryClient.refetchQueries({ queryKey: ["getGroupMembers", id] });
      setToast({
        text1: t("LABEL_MEMBER_ADDED"),
        type: "success",
      });
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  const handleRemoveMembership = async (
    membershipId: string,
    toastKey: string = "LABEL_GROUP_LEFT",
    removedUserId?: string,
  ) => {
    if (!membershipId || isActionPending) return;
    try {
      setIsActionPending(true);
      await removeGroupMembership(membershipId);

      // Notify removed user (skip if they left themselves)
      if (removedUserId && removedUserId !== user?.itemId) {
        sendNotification({
          userIds: [removedUserId],
          denormalizedPayload: JSON.stringify({
            type: "removed_from_group",
            message: t("NOTIFY_REMOVED_FROM_GROUP", {
              groupName: group?.name,
            }),
            actorAvatar: user?.profileImageUrl,
            actorName: `${user?.firstName} ${user?.lastName}`,
            actorId: user?.itemId,
          }),
          configuratoinName: "post-liked",
          responseKey: "group-action",
          responseValue: "removed_from_group",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["getGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount", id] });
      queryClient.invalidateQueries({ queryKey: ["getMyMembership", id] });
      queryClient.invalidateQueries({ queryKey: ["getAllMyMemberships", id] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMembers", id] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberIds", id] });
      queryClient.invalidateQueries({ queryKey: ["getPendingMembers", id] });
      setToast({ text1: t(toastKey), type: "success" });
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!id || isActionPending) return;
    try {
      setIsActionPending(true);
      await deleteGroup(id);

      queryClient.invalidateQueries({ queryKey: ["getGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getLatestGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getUserGroupMemberships"] });
      queryClient.invalidateQueries({ queryKey: ["getAllMyMemberships"] });
      queryClient.invalidateQueries({ queryKey: POSTS_QUERY_KEY });

      // Group no longer exists — return to the groups list.
      router.replace("/groups");
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  return {
    group,
    isPending,

    isAdmin,
    isMember,
    myMembership,
    status,

    activeTab,
    handleTabChange,

    latestGroups,
    handleSuggestion,

    toast,
    setToast,

    addMembership,
    handleJoinGroup,
    handleRemoveMembership,
    handleDeleteGroup,
    isActionPending,

    invitedMembers,
    existingMemberIds,
    membersModalVisible,
    setMembersModalVisible,
    handleAddMembers,
  };
};
